/** Pure event-context schema walker + multi-source merge.
 *
 * Port of the dashboard's `getKeysAndMacros`
 * (dashboard/src/components/CodeEditor.js) generalized to structured entries
 * so the same walk feeds completions, hovers, and a future schema-browser
 * drawer. No @codemirror imports here — safe for the sync chunk.
 */

export interface SchemaPathEntry {
  path: string;
  kind: 'leaf' | 'array' | 'object';
  description?: string;
  examples?: unknown[];
  htmlFormat: boolean;
  /** For arrays: the leaf keys of one item (dotted for one nesting level). */
  itemLeafs?: { key: string; description?: string }[];
}

/** Schema vocabulary keys — never variable-path segments of their own node. */
export const STRUCTURAL_KEYS = new Set([
  'properties',
  'items',
  'description',
  'type',
  'format',
  'examples',
  'oneOf',
  'required',
  '$schema',
  '$id',
  'title',
]);

interface SchemaNode {
  properties?: Record<string, unknown>;
  items?: unknown;
  description?: unknown;
  format?: unknown;
  examples?: unknown;
  oneOf?: unknown;
}

function asNode(value: unknown): SchemaNode | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as SchemaNode;
}

function strOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function itemLeafsOf(items: unknown): { key: string; description?: string }[] {
  const node = asNode(items);
  const leafs: { key: string; description?: string }[] = [];
  if (!node || !node.properties) return leafs;
  for (const [key, raw] of Object.entries(node.properties)) {
    const child = asNode(raw);
    if (!child) continue;
    if (child.properties) {
      // One level of drill-down into object items (dashboard's deepRecursion).
      for (const [subKey, subRaw] of Object.entries(child.properties)) {
        const sub = asNode(subRaw);
        if (sub && !sub.properties) {
          leafs.push({ key: `${key}.${subKey}`, description: strOrUndefined(sub.description) });
        }
      }
    } else {
      leafs.push({ key, description: strOrUndefined(child.description) });
    }
  }
  return leafs;
}

/** Walk a compiled event-context schema into flat path entries. */
export function walkSchema(schema: unknown): SchemaPathEntry[] {
  const entries: SchemaPathEntry[] = [];
  const seen = new Set<string>();

  function push(entry: SchemaPathEntry): void {
    const dedupeKey = `${entry.kind} ${entry.path}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    entries.push(entry);
  }

  function visit(raw: unknown, path: string): void {
    const node = asNode(raw);
    if (!node) return;

    const description = strOrUndefined(node.description);
    const examples = Array.isArray(node.examples) ? node.examples : undefined;
    const htmlFormat = node.format === 'html';

    if (path && node.items !== undefined) {
      const entry: SchemaPathEntry = { path, kind: 'array', htmlFormat, itemLeafs: itemLeafsOf(node.items) };
      if (description !== undefined) entry.description = description;
      if (examples !== undefined) entry.examples = examples;
      push(entry);
    } else if (path && node.properties) {
      // Intermediate object prefix — enables drill-down completions.
      const entry: SchemaPathEntry = { path, kind: 'object', htmlFormat: false };
      if (description !== undefined) entry.description = description;
      push(entry);
    } else if (path && description !== undefined && !node.properties) {
      const entry: SchemaPathEntry = { path, kind: 'leaf', htmlFormat, description };
      if (examples !== undefined) entry.examples = examples;
      push(entry);
    }

    if (node.properties && node.items === undefined) {
      // Keys inside `properties` are always data keys — even ones spelled like
      // schema vocabulary (the real schemas have e.g. `tour.description` as a
      // genuine field). STRUCTURAL_KEYS only guards a node's own keys, and the
      // walk never recurses those generically (properties/items/oneOf are
      // handled explicitly above), so every property extends the path.
      for (const [key, value] of Object.entries(node.properties)) {
        visit(value, path ? `${path}.${key}` : key);
      }
    }

    if (Array.isArray(node.oneOf)) {
      for (const branch of node.oneOf) visit(branch, path);
    }
  }

  visit(schema, '');
  return entries;
}

export interface MergedPathEntry {
  path: string;
  kind: SchemaPathEntry['kind'];
  description?: string;
  examples?: unknown[];
  /** Source ids in which this path exists. */
  sources: string[];
  /** format:html in EVERY source that has the path (gates the |safe apply). */
  htmlInAll: boolean;
  htmlInSome: boolean;
  conflict?: 'kind' | 'description';
  itemLeafs?: { key: string; description?: string }[];
}

/** Merge per-source walks into a single path → entry map. */
export function mergeSchemaPaths(
  inputs: { id: string; entries: SchemaPathEntry[] }[],
): Map<string, MergedPathEntry> {
  const merged = new Map<string, MergedPathEntry>();

  for (const { id, entries } of inputs) {
    for (const entry of entries) {
      const existing = merged.get(entry.path);
      if (!existing) {
        const next: MergedPathEntry = {
          path: entry.path,
          kind: entry.kind,
          sources: [id],
          htmlInAll: entry.htmlFormat,
          htmlInSome: entry.htmlFormat,
        };
        if (entry.description !== undefined) next.description = entry.description;
        if (entry.examples !== undefined) next.examples = entry.examples;
        if (entry.itemLeafs !== undefined) next.itemLeafs = entry.itemLeafs;
        merged.set(entry.path, next);
        continue;
      }

      if (!existing.sources.includes(id)) existing.sources.push(id);
      existing.htmlInAll = existing.htmlInAll && entry.htmlFormat;
      existing.htmlInSome = existing.htmlInSome || entry.htmlFormat;

      if (existing.kind !== entry.kind) {
        existing.conflict = 'kind';
      } else if (
        existing.description !== undefined &&
        entry.description !== undefined &&
        existing.description !== entry.description &&
        existing.conflict === undefined
      ) {
        existing.conflict = 'description';
      }

      if (existing.description === undefined && entry.description !== undefined) {
        existing.description = entry.description;
      }
      if (existing.examples === undefined && entry.examples !== undefined) {
        existing.examples = entry.examples;
      }
      if (entry.itemLeafs !== undefined) {
        if (existing.itemLeafs === undefined) {
          existing.itemLeafs = entry.itemLeafs;
        } else {
          const known = new Set(existing.itemLeafs.map((l) => l.key));
          for (const leaf of entry.itemLeafs) {
            if (!known.has(leaf.key)) existing.itemLeafs.push(leaf);
          }
        }
      }
    }
  }

  return merged;
}
